import { FC, useMemo, useState } from 'react';
import { usePagination, useSortBy, useTable } from 'react-table';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as CancelIcon } from 'assets/icons/cancel.svg';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import Pagination, { ITablePaginate } from 'components/Tables/Pagination';
import useRoiPractitionerPerformance from 'hooks/useRoiPractitionerPerformance';
import { formatUsdCurrency } from 'utils/formatUsdCurrency';
import MetricCell from '../MetricCell/MetricCell';

interface PractitionerPerformanceProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const PAGE_SIZE = 10;

const PractitionerPerformance: FC<PractitionerPerformanceProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const [search, setSearch] = useState('');

  const { data, isLoading, isValidating } = useRoiPractitionerPerformance({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    clinicIds,
  });

  const practitioners = useMemo(
    () => data?.periodData?.practitionerPerformance ?? [],
    [data]
  );

  const filtered = useMemo(
    () =>
      practitioners.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.specialties.some((s) =>
            s.toLowerCase().includes(search.toLowerCase())
          )
      ),
    [practitioners, search]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Practitioner',
        accessor: (row: any) => row.name,
        id: 'name',
      },
      {
        Header: 'Specialty',
        accessor: (row: any) => row.specialties.join(', '),
        id: 'specialties',
        disableSortBy: true,
      },
      {
        Header: 'Appts Requested',
        accessor: (row: any) => row.totalAppointments,
        id: 'totalAppointments',
        isMetric: true,
        Cell: ({ value, data }: any) => (
          <MetricCell
            value={value}
            max={Math.max(...data.map((d: any) => d.totalAppointments), 0)}
          />
        ),
      },
      {
        Header: 'New Patients',
        accessor: (row: any) => row.totalNewPatients,
        id: 'totalNewPatients',
        isMetric: true,
        Cell: ({ value, data }: any) => (
          <MetricCell
            value={value}
            max={Math.max(...data.map((d: any) => d.totalNewPatients), 0)}
          />
        ),
      },
      {
        Header: 'Existing Patients',
        accessor: (row: any) => row.totalExistingPatients,
        id: 'totalExistingPatients',
        isMetric: true,
        Cell: ({ value, data }) => (
          <MetricCell
            value={value}
            max={Math.max(...data.map((d: any) => d.totalExistingPatients), 0)}
          />
        ),
      },
      {
        Header: 'Production',
        accessor: (row: any) => row.totalProduction,
        id: 'totalProduction',
        isMetric: true,
        Cell: ({ value, data }: any) => (
          <MetricCell
            value={value}
            max={Math.max(...data.map((d: any) => d.totalProduction), 0)}
            format={(v) => formatUsdCurrency(v)}
          />
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filtered,
      initialState: { pageSize: PAGE_SIZE },
      disableSortRemove: true,
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="p-2.4 shadow-elevation-06-hover bg-white">
      <div className="flex items-center justify-between mb-1.6">
        <span className="text-16 font-bold leading-[2.4rem]">
          Practitioner Performance
        </span>
        <div className="flex flex-row item-centers w-[320px] min-h-[4.8] rounded-[0.8rem] border border-lightest-grey shadow-elevation-07 py-[13px] px-[19px]">
          <SearchIcon className="mr-[1rem]" width={20} />
          <input
            value={search}
            placeholder="Search..."
            className="text-14 placeholder-mid-grey focus:outline-none focus-visible:outline-none w-full"
            onChange={(e) => {
              setSearch(e.target.value);
              gotoPage(0);
            }}
            onKeyDown={(e) => e.stopPropagation()}
          />
          {search && (
            <CancelIcon
              className="ml-[1rem] cursor-pointer stroke-white"
              width={20}
              onClick={() => {
                setSearch('');
                gotoPage(0);
              }}
            />
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table
          {...getTableProps()}
          className="w-full text-14 leading-[2.1rem] table-fixed"
        >
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <thead className="font-bold">
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className="border-b border-light-grey"
              >
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="text-left p-0 py-1.9 px-1.6"
                  >
                    <div
                      className={`flex items-center gap-x-0.9 ${
                        (column as any).isMetric ? 'justify-end' : ''
                      } ${column.canSort ? 'cursor-pointer' : ''}`}
                    >
                      <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                        {column.render('Header')}
                      </span>
                      {column.canSort && (
                        <SortArrow
                          className={`${
                            column.isSorted ? 'fill-magenta' : 'fill-[#9CA3AF]'
                          } ${
                            column.isSorted && !column.isSortedDesc
                              ? 'rotate-180'
                              : ''
                          } transition-all`}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {isLoading || isValidating ? (
            <tbody>
              <tr>
                <td colSpan={6}>
                  <div className="w-full h-[24rem] flex justify-center items-center">
                    <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : filtered.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={6}>
                  <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center">
                    <EmptyBucket />
                    <span>No practitioner available on this list</span>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody {...getTableBodyProps()} className="text-darkest-grey">
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className="bg-white border-b border-light-grey last:border-0 group"
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className="text-left p-0 py-1.9 px-1.6"
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>

      {!isLoading && filtered.length > 0 && (
        <Pagination
          data={{
            currPage: pageIndex + 1,
            perPage: pageSize,
            total: filtered.length,
          }}
          handlePaginating={(paginate: ITablePaginate) => {
            gotoPage(paginate.currPage - 1);
            setPageSize(paginate.perPage);
          }}
        />
      )}
    </div>
  );
};

export default PractitionerPerformance;
